import React from 'react';
import { CheckCircle2 } from 'lucide-react';
import {
  featuresSectionHeading,
  featuresSectionSubheading,
  featuresShowcaseItems,
} from './landingContent';

type ShowcaseKey = (typeof featuresShowcaseItems)[number]['key'];

function ShowcasePhonePreview({
  title,
  mediaSrc,
  mediaAlt,
}: {
  title: string;
  mediaSrc: string;
  mediaAlt: string;
}) {
  const [hasError, setHasError] = React.useState(false);

  return (
    <div className="w-full max-w-[340px] mx-auto">
      <div className="rounded-3xl border border-stone-200 bg-[#0F1115] p-3 shadow-card">
        <div className="rounded-[1.35rem] bg-cream overflow-hidden">
          {!hasError ? (
            <img
              src={mediaSrc}
              alt={mediaAlt}
              className="w-full h-auto block"
              loading="lazy"
              onError={() => setHasError(true)}
            />
          ) : (
            <div className="aspect-[9/16] w-full flex items-center justify-center p-6 text-center">
              <div>
                <p className="text-dark font-bold mb-2">Add screenshots or GIFs</p>
                <p className="text-stone text-sm leading-relaxed max-w-xs mx-auto">
                  Put files in <span className="font-semibold">public/assets/showcase</span> and set the paths in{' '}
                  <span className="font-semibold">landingContent.ts</span>.
                </p>
                <p className="text-stone text-xs mt-3">
                  Selected: <span className="font-semibold">{mediaSrc}</span>
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
      <p className="text-xs text-stone mt-3 text-center">{title}</p>
    </div>
  );
}

export const FeaturesGroupedSection: React.FC = () => {
  const [activeKey, setActiveKey] = React.useState<ShowcaseKey>(featuresShowcaseItems[0]?.key ?? 'roster');
  const active = featuresShowcaseItems.find((x) => x.key === activeKey) ?? featuresShowcaseItems[0];

  const setActiveAndScroll = React.useCallback((key: ShowcaseKey) => {
    setActiveKey(key);
    window.setTimeout(() => {
      const el = document.getElementById(`showcase-panel-${key}`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 0);
  }, []);

  return (
    <section className="px-4 py-12 md:py-16 bg-white border-y border-stone-200/80">
      <div className="max-w-7xl mx-auto">
        <div className="w-full flex justify-center mb-4 md:mb-6">
          <div className="h-2 w-32 rounded-full bg-gradient-to-r from-primary/70 via-secondary/70 to-primary/30" />
        </div>
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-3 max-w-3xl mx-auto leading-tight text-primary">
          {featuresSectionHeading}
        </h2>
        <p className="text-center text-stone text-lg mb-12 md:mb-14 max-w-2xl mx-auto">
          {featuresSectionSubheading}
        </p>

        {/* Showcase */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10 items-start mb-14 md:mb-16">
          <div className="order-2 lg:order-1">
            <div className="flex flex-col gap-3">
              {featuresShowcaseItems.map((item) => {
                const isActive = item.key === activeKey;
                return (
                  <div key={item.key} className="w-full">
                    <button
                      type="button"
                      onClick={() => setActiveAndScroll(item.key)}
                      className={[
                        'text-left rounded-2xl border transition-colors p-4 w-full',
                        isActive
                          ? 'border-primary bg-primary/5'
                          : 'border-stone-200 bg-white hover:bg-cream/50',
                      ].join(' ')}
                      aria-expanded={isActive}
                      aria-controls={`showcase-panel-${item.key}`}
                    >
                      <div className="flex items-start gap-3">
                        <CheckCircle2
                          className={[
                            'w-5 h-5 mt-0.5 flex-shrink-0',
                            isActive ? 'text-primary' : 'text-stone',
                          ].join(' ')}
                          aria-hidden
                        />
                        <div className="min-w-0">
                          <p className="font-bold text-dark">{item.title}</p>
                          <p className="text-sm text-stone leading-relaxed mt-1">{item.description}</p>
                        </div>
                      </div>
                    </button>

                    {/* Mobile accordion preview */}
                    <div className={['lg:hidden mt-4', isActive ? 'block' : 'hidden'].join(' ')}>
                      <div id={`showcase-panel-${item.key}`}>
                        <ShowcasePhonePreview
                          title={item.title}
                          mediaSrc={item.mediaSrc}
                          mediaAlt={item.mediaAlt}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Desktop preview (phone sized) */}
          <div className="order-1 lg:order-2 hidden lg:block">
            {active ? (
              <div className="flex justify-center lg:justify-end">
                <ShowcasePhonePreview
                  title={active.title}
                  mediaSrc={active.mediaSrc}
                  mediaAlt={active.mediaAlt}
                />
              </div>
            ) : null}
          </div>
        </div>

        {/* Tools offering moved into the Trust section below */}
      </div>
    </section>
  );
};
